import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import type { FundingApplicationSchemas } from "api-types";
import { axiosClient, useAxios } from "auth-sdk";
import type { AxiosInstance, AxiosResponse } from "axios";

import type { FormattedFundingApplicationScreenUpdate } from "./types";

export const FUNDING_APPLICATION_API = "/api/:customerSlug/funding/api/application/";
export const FUNDING_APPLICATION_CONFIGURATION_API = "/api/:customerSlug/funding/api/application-configuration/";
export const FUNDING_APPLICATION_CONFIGURATION_COMPLETION_STATUS_API =
  "/api/:customerSlug/funding/api/application-configuration/completion-status";

export const getFundingApplicationQueryOptions = (customerDomain: string) => {
  const url = FUNDING_APPLICATION_API.replace(":customerSlug", customerDomain);
  return queryOptions({
    queryKey: [url, customerDomain],
    queryFn: () =>
      axiosClient.get<FundingApplicationSchemas.CustomerFundingApplicationScreen>(url).then(({ data }) => data),
  });
};

export const getFundingApplicationStagesQueryOptions = (customerDomain: string) => {
  const url = FUNDING_APPLICATION_CONFIGURATION_API.replace(":customerSlug", customerDomain);
  return queryOptions({
    queryKey: [url],
    queryFn: () => axiosClient.get<FundingApplicationSchemas.CustomerFundingApplicationStagesScreen>(url),
    select: ({
      data,
    }: {
      data: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen;
    }): FundingApplicationSchemas.CustomerFundingApplicationStagesScreen => data,
    refetchOnMount: true,
  });
};

const submitFundingApplicationData = (
  client: AxiosInstance,
  mutationUrl: string,
  data: FundingApplicationSchemas.CustomerFundingApplicationUpdate
): Promise<FundingApplicationSchemas.CustomerFundingApplicationScreen> => {
  const url = mutationUrl + data.id.toString();
  return client
    .patch<
      FundingApplicationSchemas.CustomerFundingApplicationUpdate,
      AxiosResponse<FundingApplicationSchemas.CustomerFundingApplicationScreen>
    >(url, data)
    .then(({ data }: AxiosResponse<FundingApplicationSchemas.CustomerFundingApplicationScreen>) => data);
};

// Omit onSuccess when running multiple mutations in parallel
export const useFundingApplicationMutation = (customerDomain: string) => {
  const client = useAxios();
  const queryClient = useQueryClient();
  const mutationUrl = FUNDING_APPLICATION_API.replace(":customerSlug", customerDomain);

  return useMutation({
    mutationKey: ["updateFundingApplication"],
    mutationFn: (data: FormattedFundingApplicationScreenUpdate) => {
      const websiteUrlsStr = data.websiteUrls ? data.websiteUrls.join(",") : undefined;
      const intendedUseStr = data.intendedUse ? data.intendedUse.join(",") : undefined;
      const updatedData: FundingApplicationSchemas.CustomerFundingApplicationUpdate = {
        ...data,
        websiteUrls: websiteUrlsStr,
        intendedUse: intendedUseStr,
      };
      return submitFundingApplicationData(client, mutationUrl, updatedData);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: getFundingApplicationQueryOptions(customerDomain).queryKey });
      await queryClient.invalidateQueries({ queryKey: getStageCompletionStatusQueryOptions(customerDomain).queryKey });
    },
  });
};

export const getStageCompletionStatusQueryOptions = (customerDomain: string) => {
  const url = FUNDING_APPLICATION_CONFIGURATION_COMPLETION_STATUS_API.replace(":customerSlug", customerDomain);
  return queryOptions({
    queryKey: [url],
    queryFn: async (): Promise<FundingApplicationSchemas.StageCompletionStatus> => {
      const { data } = await axiosClient.get(url);
      return data;
    },
  });
};
